/* import Library and custom made hooks*/
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";

import useLanguageChange from "../../../../hooks/useLanguageChange";

/* Import all constants */
import { validSessionIDLength } from "../../../../constants/Constants";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../../../constants/PublicVariables";
import CustomToast from "../../../UI/customToast";

/* Import Services/Shared/Actions  */
import {
    allowOnlyNumericInput,
    formatSessionID,
    getValidSessionID,
    isCustomDomain,
    isIFrame,
    isThirdPartyPlatform,
} from "../../../../shared/function";
import { getPrivacyPolicy } from "../../../../actions/onLoadFunctionAction";
import { getSessionId, isMaxLimitReachedForUploadFiles } from "../../../../shared/utility";

const ERROR_CODE = {
    BAD_REQUEST: 400,
    DOMAIN_CONFIGURATION_CHANGE: 1143,
};

const EnterSessionIDForm = ({ handleToShowPrivacyPolicyPopup }) => {
    //#region Local state
    const params = useParams();
    const dispatch = useDispatch();
    const paramsSessionId = getSessionId(params?.sessionId);

    const [sessionId, setSessionId] = useState("");
    const [validSessionId, setValidSessionId] = useState("");
    const [showContinueButton, setShowContinueButton] = useState(true);
    const { isCompanyAvailableData, privacyPolicyData, isCompanyAvailableError } = useSelector((state) => state.onLoadData);
    const { isCallDisconnected } = useSelector((state) => state.commonReducer);
    const { joinMeetingError } = useSelector((state) => state.joinMeeting);

    /* custome hook for getting updated localized value object for entire project 
    This will give localize value object for selected language
    Also will give updated localized value object when language change*/
    const localisedValues = useLanguageChange();

    //#endregion Local state

    //#region Local hooks

    /* Handle the privacy api on load if is company api call successfully and handle for global domain */
    useEffect(() => {
        if (
            paramsSessionId &&
            (isCompanyAvailableData || !isCustomDomain()) &&
            !GLOBAL_MEETING_DATA.sessionId &&
            GLOBAL_MEETING_DATA.enablePolicyPopup === undefined
        ) {
            sessionIDChangeHandler(paramsSessionId, false);
            if (!GLOBAL_MEETING_DATA.privacyPolicyAgreed) {
                GLOBAL_MEETING_DATA.sessionId = paramsSessionId;
                dispatch(getPrivacyPolicy(paramsSessionId));
            }
        }
    }, [isCompanyAvailableData]);

    /* Handle to set session code when company api failed */
    useEffect(() => {
        if (isCompanyAvailableError && paramsSessionId && !GLOBAL_MEETING_DATA.sessionId && GLOBAL_MEETING_DATA.enablePolicyPopup === undefined) {
            sessionIDChangeHandler(paramsSessionId, false);
            GLOBAL_MEETING_DATA.sessionId = paramsSessionId;
            CustomToast.error(isCompanyAvailableError.message);
        }
    }, [isCompanyAvailableError]);

    /* Show/Hide Continue button on joinMeetingError */
    useEffect(() => {
        if (joinMeetingError) {
            if (joinMeetingError.status === ERROR_CODE.BAD_REQUEST && ERROR_CODE.DOMAIN_CONFIGURATION_CHANGE) {
                setShowContinueButton(false);
            } else {
                setShowContinueButton(true);
            }
        }
    }, [joinMeetingError]);

    /* Privacy api call after enter nine digit valid code */
    useEffect(() => {
        if (validSessionId.length === validSessionIDLength) {
            GLOBAL_MEETING_DATA.sessionId = validSessionId;
            dispatch(getPrivacyPolicy(validSessionId));
        }
    }, [validSessionId]);

    //#endregion Local hooks

    /* Handle the session code with validation and formatter */
    const sessionIDChangeHandler = (sessionId, validSessionIdState = true) => {
        const validSessionID = getValidSessionID(sessionId);
        GLOBAL_MEETING_DATA.sessionId = validSessionID;
        if (validSessionIdState) {
            setValidSessionId(validSessionID);
        }
        const validFormattedSessionID = formatSessionID(validSessionID);
        setSessionId(validFormattedSessionID);
        CALL_DATA.participantUserName = ""; //Reset guest name on session id change
    };

    // Check home screen button available or not
    const shouldShowHomeScreenButton = () => {
        //old - isHomeScreenbtnAvailable
        return (
            GLOBAL_MEETING_DATA.isAgent &&
            GLOBAL_MEETING_DATA.homeScreenButtonParams === 1 &&
            isEmpty(GLOBAL_MEETING_DATA.redirectUrl) &&
            isEmpty(GLOBAL_MEETING_DATA.platformName) &&
            !isThirdPartyPlatform() &&
            !isIFrame()
        );
    };

    const goBack = () => {
        window.history.go(-1);
    };

    const isShowCloseTabButton = () => {
        return GLOBAL_MEETING_DATA.redirectUrl && GLOBAL_MEETING_DATA.platformName && !isIFrame();
    };

    const redirectURLForSalesforce = () => {
        if (GLOBAL_MEETING_DATA.redirectParams || GLOBAL_MEETING_DATA.closetabParams == 1) {
            if (window) {
                window.close();
            }
        }
    };

    const getContinueButtonText = () => {
        if (isCallDisconnected) {
            if (PublicVariables.isOnlyUploadImageMeet) {
                return localisedValues?.upload_files_msg;
            } else {
                return localisedValues?.reconnect;
            }
        } else {
            return localisedValues?.continue_initcap;
        }
    };

    /* remove btn div if the no ant button are available*/
    const isBtnDivVisible = () => {
        return (
            (privacyPolicyData &&
                !isMaxLimitReachedForUploadFiles() &&
                (validSessionId.length || GLOBAL_MEETING_DATA.sessionId.length) === validSessionIDLength) ||
            shouldShowHomeScreenButton() ||
            isShowCloseTabButton()
        );
    };

    return (
        <div className='session-form'>
            {!isCallDisconnected && (
                <div className='enter-sessionid-form'>
                    <div className='floating-form-group'>
                        <div className='floating-form-group-in'>
                            <input
                                className='floating-form-control'
                                type='text'
                                placeholder={localisedValues?.enter_code}
                                name='sessionid'
                                id='sessionid'
                                value={sessionId || formatSessionID(GLOBAL_MEETING_DATA.sessionId)}
                                onChange={(event) => sessionIDChangeHandler(event.target.value)}
                                onKeyDown={(event) => allowOnlyNumericInput(event)}
                                data-testid='sessionid'
                                style={{ fontSize: PublicVariables.isIOSDevice ? "16px" : "" }}
                            />
                            <span className='floating-label'>
                                <span className='floating-label-in'>{localisedValues?.enter_code}</span>
                            </span>
                            <span className='for-border'></span>
                        </div>
                    </div>
                </div>
            )}
            {isBtnDivVisible() && (
                <div className='session-form-button-row'>
                    {privacyPolicyData &&
                        showContinueButton &&
                        !isMaxLimitReachedForUploadFiles() &&
                        (validSessionId.length || GLOBAL_MEETING_DATA.sessionId.length) === validSessionIDLength && (
                            <div className='button'>
                                <button className='btn btn-action' data-testid='continueBtn' onClick={() => handleToShowPrivacyPolicyPopup()}>
                                    {getContinueButtonText()}
                                </button>
                            </div>
                        )}
                    {shouldShowHomeScreenButton() && (
                        <div className='button'>
                            <button className='btn btn-primary' data-testid='homeBtn' onClick={() => goBack()}>
                                {localisedValues?.home_screen}
                            </button>
                        </div>
                    )}
                    {isShowCloseTabButton() && (
                        <div className='button'>
                            <button className='btn btn-primary' data-testid='closeBtn' onClick={() => redirectURLForSalesforce()}>
                                {localisedValues?.close_tab}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

EnterSessionIDForm.propTypes = {
    handleToShowPrivacyPolicyPopup: PropTypes.func.isRequired,
};

export default EnterSessionIDForm;
